import React, { useState } from 'react';
import { 
  Smartphone, 
  Wifi, 
  WifiOff, 
  Shield, 
  Stethoscope, 
  Video, 
  Glasses,
  CreditCard,
  CheckCircle,
  Download
} from 'lucide-react';

// Feature Hooks and Contexts
import { useScreenSize } from '../hooks/useResponsive';
import { useOffline } from '../pwa/useOffline';
import { usePhysicianInLoop } from '../contexts/PhysicianInLoopContext';
import { usePHIProtection } from '../contexts/PHIProtectionContext';
import { useTelemedicine } from '../contexts/TelemedicineContext';
import { useVR, VR_SCENARIOS } from '../contexts/VRContext';
import { getStripeConfig, TEST_CARDS } from '../lib/stripe';

// Components
import { VerificationDialog } from '../components/VerificationDialog';

export default function FeaturesDemo() {
  const [activeFeature, setActiveFeature] = useState<number | null>(null);
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🧪 Features Demo
          </h1>
          <p className="text-gray-600">
            Test all 7 newly implemented features
          </p>
        </div>
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <FeatureCard 
            number={1}
            title="PWA & Offline"
            icon={<Download className="w-6 h-6" />}
            color="blue"
            isActive={activeFeature === 1}
            onClick={() => setActiveFeature(activeFeature === 1 ? null : 1)}
          >
            <PWADemo />
          </FeatureCard>
          
          <FeatureCard 
            number={2}
            title="Mobile Responsiveness"
            icon={<Smartphone className="w-6 h-6" />}
            color="green"
            isActive={activeFeature === 2}
            onClick={() => setActiveFeature(activeFeature === 2 ? null : 2)}
          >
            <MobileDemo />
          </FeatureCard>
          
          <FeatureCard 
            number={3}
            title="Physician-in-the-Loop"
            icon={<Stethoscope className="w-6 h-6" />}
            color="purple"
            isActive={activeFeature === 3}
            onClick={() => setActiveFeature(activeFeature === 3 ? null : 3)}
          >
            <PhysicianDemo />
          </FeatureCard>
          
          <FeatureCard 
            number={4}
            title="Stripe Test Mode"
            icon={<CreditCard className="w-6 h-6" />}
            color="orange"
            isActive={activeFeature === 4}
            onClick={() => setActiveFeature(activeFeature === 4 ? null : 4)}
          >
            <StripeDemo />
          </FeatureCard>
          
          <FeatureCard 
            number={5}
            title="PHI Protection"
            icon={<Shield className="w-6 h-6" />}
            color="red"
            isActive={activeFeature === 5}
            onClick={() => setActiveFeature(activeFeature === 5 ? null : 5)}
          >
            <PHIDemo />
          </FeatureCard>
          
          <FeatureCard 
            number={6}
            title="Telemedicine"
            icon={<Video className="w-6 h-6" />}
            color="teal"
            isActive={activeFeature === 6}
            onClick={() => setActiveFeature(activeFeature === 6 ? null : 6)}
          >
            <TelemedicineDemo />
          </FeatureCard>
          
          <FeatureCard 
            number={7}
            title="VR Simulation"
            icon={<Glasses className="w-6 h-6" />}
            color="indigo"
            isActive={activeFeature === 7}
            onClick={() => setActiveFeature(activeFeature === 7 ? null : 7)}
          >
            <VRDemo />
          </FeatureCard>
        </div>
      </div>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ 
  number, 
  title, 
  icon, 
  color, 
  isActive, 
  onClick, 
  children 
}: {
  number: number;
  title: string;
  icon: React.ReactNode;
  color: string;
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
    teal: 'bg-teal-100 text-teal-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  };
  
  return (
    <div className={`bg-white rounded-xl shadow-sm border-2 transition-all ${
      isActive ? 'border-blue-500 shadow-lg' : 'border-gray-200'
    }`}>
      <button 
        onClick={onClick}
        className="w-full p-4 flex items-center gap-4 text-left"
      >
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <span className="text-sm text-gray-500">Feature {number}</span>
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="ml-auto">
          <CheckCircle className={`w-5 h-5 ${isActive ? 'text-blue-500' : 'text-gray-300'}`} />
        </div>
      </button>
      
      {isActive && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
}

// Individual Feature Demos
function PWADemo() {
  const { isOnline, isFullySynced, lastSync } = useOffline();
  
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-2">
        {isOnline ? (
          <><Wifi className="w-4 h-4 text-green-500" /> Online</>
        ) : (
          <><WifiOff className="w-4 h-4 text-red-500" /> Offline</>
        )}
      </div>
      <div>Synced: {isFullySynced ? '✅ Yes' : '❌ No'}</div>
      <div>Last sync: {lastSync?.toLocaleString() || 'Never'}</div>
      <p className="text-gray-500 mt-2">
        Open DevTools → Application → Service Workers to see PWA status
      </p>
    </div>
  );
}

function MobileDemo() {
  const { width, height, breakpoint, isMobile, isTablet, isDesktop } = useScreenSize();
  
  return (
    <div className="space-y-2 text-sm">
      <div>Screen: {width} x {height}</div>
      <div>Breakpoint: <span className="font-mono bg-gray-100 px-1 rounded">{breakpoint}</span></div>
      <div className="flex gap-2 mt-2">
        <span className={`px-2 py-1 rounded ${isMobile ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}>
          Mobile
        </span>
        <span className={`px-2 py-1 rounded ${isTablet ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}>
          Tablet
        </span>
        <span className={`px-2 py-1 rounded ${isDesktop ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}>
          Desktop
        </span>
      </div>
      <p className="text-gray-500 mt-2">
        Resize browser or use DevTools device mode to test
      </p>
    </div>
  );
}

function PhysicianDemo() {
  const { createDecision, pendingDecisions, decisionHistory } = usePhysicianInLoop();
  const [showVerification, setShowVerification] = useState(false);
  
  const handleCreateDecision = () => {
    createDecision({
      type: 'dosing',
      category: 'medication',
      description: 'Prescribe Metformin 500mg for Type 2 Diabetes',
      aiRecommendation: 'Start with 500mg once daily with meals, titrate to 1000mg twice daily over 4 weeks',
      aiConfidence: 'high',
      evidenceLevel: 'A',
      riskLevel: 'moderate'
    });
    setShowVerification(true);
  };
  
  return (
    <div className="space-y-3 text-sm">
      <button
        onClick={handleCreateDecision}
        className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
      >
        Test Clinical Decision
      </button>
      <div>Pending: {pendingDecisions.length}</div>
      <div>History: {decisionHistory.length}</div>
      
      {showVerification && pendingDecisions[0] && (
        <VerificationDialog 
          decision={pendingDecisions[0]} 
          onClose={() => setShowVerification(false)} 
        />
      )}
    </div>
  );
}

function StripeDemo() {
  const config = getStripeConfig();
  
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          config.isTestMode ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
        }`}>
          {config.mode.toUpperCase()} MODE
        </span>
      </div>
      <div className="bg-gray-50 p-2 rounded font-mono text-xs">
        Test Card: {TEST_CARDS.success}
      </div>
      <a 
        href="/pricing" 
        className="block w-full py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-center"
      >
        Go to Pricing →
      </a>
    </div>
  );
}

function PHIDemo() {
  const { containsPHI, redactPHI, session } = usePHIProtection();
  const [testInput, setTestInput] = useState('');
  const [result, setResult] = useState<string | null>(null);
  
  const handleTest = () => {
    const hasPHI = containsPHI(testInput);
    const redacted = redactPHI(testInput);
    setResult(hasPHI ? `PHI Detected! Redacted: ${redacted}` : 'No PHI detected');
  };
  
  return (
    <div className="space-y-3 text-sm">
      <input
        type="text"
        value={testInput}
        onChange={(e) => setTestInput(e.target.value)}
        placeholder="Try: John Doe, john@email.com, 123-45-6789"
        className="w-full px-3 py-2 border rounded-lg text-sm"
      />
      <button
        onClick={handleTest}
        className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
      >
        Scan for PHI
      </button>
      {result && (
        <div className={`p-2 rounded ${result.includes('Detected') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {result}
        </div>
      )}
      <div className="text-gray-500">
        Session locked: {session.isLocked ? 'Yes' : 'No'}
      </div>
    </div>
  );
}

function TelemedicineDemo() {
  const { status, startLocalMedia, localStream, stopLocalMedia, toggleMute, isMuted } = useTelemedicine();
  
  return (
    <div className="space-y-3 text-sm">
      <div>Status: <span className="font-medium">{status}</span></div>
      
      {!localStream ? (
        <button
          onClick={startLocalMedia}
          className="w-full py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
        >
          Start Camera
        </button>
      ) : (
        <div className="space-y-2">
          <div className="bg-black rounded-lg aspect-video overflow-hidden">
            <video
              autoPlay
              playsInline
              muted
              ref={(el) => { if (el) el.srcObject = localStream; }}
              className="w-full h-full object-cover transform scale-x-[-1]"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={toggleMute}
              className={`flex-1 py-2 rounded-lg ${isMuted ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}
            >
              {isMuted ? 'Unmute' : 'Mute'}
            </button>
            <button
              onClick={stopLocalMedia}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg"
            >
              Stop
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VRDemo() {
  const { isVRSupported, sessionState } = useVR();
  
  return (
    <div className="space-y-3 text-sm">
      <div>VR Supported: {isVRSupported ? '✅ Yes' : '❌ No'}</div>
      <div>State: {sessionState}</div>
      
      {!isVRSupported && (
        <div className="bg-amber-50 p-2 rounded text-amber-700 text-xs">
          VR requires a WebXR-compatible device (Quest, Vive, etc.)
        </div>
      )}
      
      <div className="text-gray-500">
        Available scenarios:
      </div>
      <ul className="text-xs space-y-1">
        {VR_SCENARIOS.slice(0, 3).map(s => (
          <li key={s.id} className="flex items-center gap-1">
            <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
            {s.title}
          </li>
        ))}
      </ul>
    </div>
  );
}
